import React, { useEffect, useMemo, useState } from "react";
import { imgSrc, imgSet } from "@/lib/img";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Check, ChevronDown, Users, Gauge, Minus, Compass, MapPin } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import PastEditions from "@/components/site/PastEditions";
import ContactCTA from "@/components/site/ContactCTA";
import { Reveal, SafeImg, EASE } from "@/components/site/Reveal";
import { badgeClass } from "@/components/site/ExpeditionCard";
import { useLang, DIFFICULTY } from "@/lib/i18n";
import { L } from "@/lib/localize";
import { useAssets, tourCover, tourMedia, leaderPortrait } from "@/lib/assets";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import JsonLd from "@/components/site/JsonLd";
import { graph, breadcrumbNode, tourNodes } from "@/lib/schema";
import NotFound from "@/pages/NotFound";
import { LEGACY_TOUR_SLUGS } from "@/lib/legacySlugs";

export default function ExpeditionDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const assets = useAssets();
  const diffLabel = (d) => (DIFFICULTY[lang] && DIFFICULTY[lang][d]) || d;
  const [tour, setTour] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [openDay, setOpenDay] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/tours/${slug}`).then((r) => setTour(r.data)).catch(() => setNotFound(true));
    api.get("/leaders").then((r) => setLeaders(r.data)).catch(() => {});
  }, [slug]);

  const seoTour = tour ? L(tour, lang) : null;
  useSeo({
    title: seoTour ? `${seoTour.title} — ${seoTour.location}, ${seoTour.country} | De Los Cielos` : "Spedizione | De Los Cielos",
    description: seoTour ? (seoTour.description || "").replace(/\s+/g, " ").slice(0, 157) : "",
    image: tour ? tourCover(assets, tour) : undefined,
    path: `/viaggi/${slug}`,
    type: "article",
    noindex: notFound,
    preloadImage: tour ? imgSrc(tourCover(assets, tour), 1600) : undefined,
    preloadSrcSet: tour ? imgSet(tourCover(assets, tour), [800, 1200, 1600, 2000]) : undefined,
  });

  if (LEGACY_TOUR_SLUGS[slug]) return <Navigate to={`/viaggi/${LEGACY_TOUR_SLUGS[slug]}`} replace />;

  if (notFound) return <NotFound />;

  const destName = tour ? (tour.destination_name || tour.location || tour.country || "") : "";
  const destSlug = destName
    ? destName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
    : "";

  const jsonLd = tour && seoTour
    ? graph([
        ...tourNodes({ tour, lt: seoTour, lang }),
        breadcrumbNode([
          { name: "Home", path: "/" },
          { name: t("nav.expeditions"), path: "/viaggi" },
          { name: seoTour.title, path: `/viaggi/${tour.slug}` },
        ], lang),
      ])
    : null;

  if (!tour) {
    return <div className="bg-obsidian min-h-screen flex items-center justify-center"><div className="font-mono text-xs tracking-[0.3em] text-copper animate-slowpulse">CARICAMENTO…</div></div>;
  }

  const lt = L(tour, lang);
  const cover = tourCover(assets, tour) || (tour.images && tour.images[0]);
  const pastMedia = tourMedia(assets, tour);
  // Più tour leader per spedizione, nell'ordine configurato dal CMS
  const tourLeaders = (tour.leader_slugs || []).map((s) => leaders.find((l) => l.slug === s)).filter(Boolean).map((l) => L(l, lang));

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-tour" data={jsonLd} />

      {/* HERO */}
      <section className="relative h-[72vh] min-h-[480px] flex items-end overflow-hidden" data-testid="tour-hero">
        {cover ? (
          <img
            src={imgSrc(cover, 1600)}
            srcSet={imgSet(cover, [800, 1200, 1600, 2000])}
            sizes="100vw"
            alt={lt.title}
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
        ) : (
          <div className="absolute inset-0 bg-obsidian2 flex items-center justify-center" data-testid="tour-hero-placeholder">
            <Compass className="h-16 w-16 text-copper/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/30 to-obsidian" />
        <div className="absolute inset-0 grain" />
        <div className="container-x relative z-10 pb-14">
          <Link to="/viaggi" className="font-mono text-[11px] uppercase tracking-[0.25em] text-copperlight flex items-center gap-2 mb-5 w-max" data-testid="tour-back-link">
            <ArrowLeft className="h-4 w-4" /> {t("tours.title")}
          </Link>
          <motion.h1
            className="font-serif font-semibold text-4xl sm:text-6xl lg:text-7xl leading-[0.98] max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE }}
            data-testid="tour-title"
          >
            {lt.title}
          </motion.h1>
          <div className="mt-5 flex flex-wrap gap-2.5 items-center">
            <span className={badgeClass(tour.badge)} data-testid="tour-badge">{t(`badge.${tour.badge}`)}</span>
            <span className="chip"><CalendarDays className="h-3.5 w-3.5 text-copper" /> {lt.dates_label}</span>
            <span className="chip"><Users className="h-3.5 w-3.5 text-copper" /> {t("tours.group")} {tour.group_max}</span>
            <span className="chip"><Gauge className="h-3.5 w-3.5 text-copper" /> {diffLabel(tour.difficulty)}</span>
          </div>
        </div>
      </section>

      {/* INFO BAR */}
      <div className="border-b border-copper/20 sticky top-16 sm:top-20 z-40 backdrop-blur-md bg-obsidian/85" data-testid="tour-infobar">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 sm:gap-4 py-3 sm:py-4">
          <div className="flex flex-wrap gap-x-6 sm:gap-x-10 gap-y-2">
            <div><p className="font-mono text-[10px] uppercase tracking-widest text-sand">{t("tours.days")}</p><p className="font-serif text-lg sm:text-xl text-cream">{tour.duration_days}</p></div>
            <div><p className="font-mono text-[10px] uppercase tracking-widest text-sand">{lt.location}</p><p className="font-serif text-lg sm:text-xl text-cream">{lt.country}</p></div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-sand">{t("tours.from")}</p>
              <p className="font-serif text-lg sm:text-xl text-copper" data-testid="tour-price">€{Number(tour.price).toLocaleString("it-IT")}</p>
            </div>
          </div>
          <Link to={`/candidatura?tour=${tour.slug}`} className="btn-copper !px-4 !py-2.5 !text-xs sm:!px-6 sm:!py-3 sm:!text-sm" data-testid="tour-cta-contact">
            {t("tours.cta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* DESCRIPTION */}
      <section className="py-20 sm:py-28" data-testid="tour-description">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <p className="eyebrow">{t("home.exped.eyebrow")}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream mt-3">{lt.location}, {lt.country}</h2>
            {destSlug && (
              <Link
                to={`/destinazioni/${destSlug}`}
                className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-copper hover:text-copperlight transition-colors"
                data-testid="tour-destination-link"
              >
                <MapPin className="h-3.5 w-3.5" /> {t("dest.linkFromTour")}: {tour.destination_name || lt.location}
              </Link>
            )}
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-8">
            <p className="text-lg text-cream2 leading-relaxed whitespace-pre-line">{lt.description}</p>
          </Reveal>
        </div>
      </section>

      {/* PROGRAM */}
      <section className="pb-20 sm:pb-28" data-testid="tour-program">
        <div className="container-x">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream mb-10">{t("tours.program")}</h2>
          </Reveal>
          <div className="border-t border-white/10">
            {lt.itinerary.map((d, i) => (
              <div key={i} className="border-b border-white/10" data-testid={`program-day-${d.day}`}>
                <button className="w-full flex items-center gap-5 py-5 text-left group" onClick={() => setOpenDay(openDay === i ? -1 : i)} data-testid={`program-day-toggle-${d.day}`}>
                  <span className="font-mono text-copper text-sm w-10 shrink-0">{String(d.day).padStart(2, "0")}</span>
                  <span className="font-serif text-xl sm:text-2xl text-cream group-hover:text-copperlight transition-colors flex-1">{d.title}</span>
                  <ChevronDown className={"h-5 w-5 text-copper transition-transform duration-300 " + (openDay === i ? "rotate-180" : "")} />
                </button>
                <AnimatePresence initial={false}>
                  {openDay === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="pb-6 pl-[3.75rem] pr-4 text-cream2 leading-relaxed max-w-3xl">{d.description}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDIZIONI PRECEDENTI: foto / video / audio a scorrimento */}
      <PastEditions title={t("past.title")} photos={pastMedia.photos} videos={pastMedia.videos} audio={pastMedia.audio} alt={lt.title} testId="tour-past-editions" />

      {/* LEADER + INCLUDES */}
      <section className="py-20 sm:py-28">
        <div className={"container-x grid grid-cols-1 gap-14 " + (tourLeaders.length ? "md:grid-cols-2" : "")}>
          {tourLeaders.length > 0 && (
          <div>
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl text-cream mb-8" data-testid="tour-leaders-title">{t("tours.leader")} <span className="font-mono text-xs text-sand align-middle ml-2">{tourLeaders.length}</span></h2>
            </Reveal>
            <div className="space-y-6" data-testid="tour-leaders-list">
              {tourLeaders.map((l) => (
                <Reveal key={l.id}>
                  <Link to={`/leader/${l.slug}`} className="group flex items-center gap-5 card-dark p-5 hover:border-copper/40 transition-colors" data-testid={`tour-leader-${l.slug}`}>
                    <SafeImg src={leaderPortrait(assets, l)} alt={l.name} className="h-20 w-20 shrink-0 object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                    <div>
                      <p className="font-serif text-2xl text-cream group-hover:text-copperlight transition-colors">{l.name}</p>
                      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-sand mt-1">{l.role}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-copper ml-auto" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <Reveal>
              <h3 className="font-serif text-2xl text-cream mb-5 flex items-center gap-2"><Check className="h-5 w-5 text-copper" /> {t("tours.included")}</h3>
              <ul className="space-y-3">
                {(lt.included || []).map((x, i) => (
                  <li key={i} className="flex gap-3 text-sm text-cream2"><Check className="h-4 w-4 text-copper shrink-0 mt-0.5" /> {x}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="font-serif text-2xl text-cream mb-5 flex items-center gap-2"><Minus className="h-5 w-5 text-sand" /> {t("tours.excluded")}</h3>
              <ul className="space-y-3">
                {(lt.excluded || []).map((x, i) => (
                  <li key={i} className="flex gap-3 text-sm text-cream2"><Minus className="h-4 w-4 text-sand shrink-0 mt-0.5" /> {x}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA / FORM */}
      <ContactCTA to={`/candidatura?tour=${tour.slug}`} bg={assets && assets.cta_bg && assets.cta_bg.tour} testId="tour-cta" compact />

      <Footer />
    </div>
  );
}
